import { useMemo } from "react";

type Lang = "html" | "json" | "text";

interface Token {
  text: string;
  className: string;
}

const DEFAULT_CLASS = "text-white/80";

function tokenize(code: string, lang: Lang): Token[] {
  if (lang === "text") return [{ text: code, className: DEFAULT_CLASS }];

  const patterns: Array<[RegExp, string]> =
    lang === "html"
      ? [
          [/^<!--[\s\S]*?-->/, "text-white/40"],
          [/^<\/?[a-zA-Z][\w.-]*/, "text-sky-300"],
          [/^\/?>/, "text-sky-300"],
          [/^[a-zA-Z][\w:-]*(?==)/, "text-violet-300"],
          [/^"[^"]*"/, "text-emerald-300"],
          [/^\s+/, DEFAULT_CLASS],
        ]
      : [
          [/^"[^"]*"(?=\s*:)/, "text-violet-300"],
          [/^"[^"]*"/, "text-emerald-300"],
          [/^-?\d+(?:\.\d+)?/, "text-amber-300"],
          [/^(?:true|false|null)\b/, "text-sky-300"],
          [/^\s+/, DEFAULT_CLASS],
        ];

  const tokens: Token[] = [];
  let rest = code;
  while (rest.length > 0) {
    let matched = false;
    for (const [re, className] of patterns) {
      const m = re.exec(rest);
      if (m && m[0].length > 0) {
        tokens.push({ text: m[0], className });
        rest = rest.slice(m[0].length);
        matched = true;
        break;
      }
    }
    if (!matched) {
      tokens.push({ text: rest.charAt(0), className: DEFAULT_CLASS });
      rest = rest.slice(1);
    }
  }
  return tokens;
}

interface CodeBlockProps {
  code: string;
  lang?: Lang;
  title?: string;
  className?: string;
}

/**
 * Zero-dependency syntax-highlighted snippet in a glass panel with a
 * macOS-style title bar. Handles the deck's short html/json fragments.
 */
export function CodeBlock({ code, lang = "html", title, className = "" }: CodeBlockProps) {
  const tokens = useMemo(() => tokenize(code, lang), [code, lang]);
  return (
    <div className={`glass overflow-hidden ${className}`}>
      <div className="flex items-center gap-2 border-b border-white/10 bg-white/5 px-5 py-3">
        <span className="h-3 w-3 rounded-full bg-accent-rose/70" />
        <span className="h-3 w-3 rounded-full bg-accent-amber/70" />
        <span className="h-3 w-3 rounded-full bg-accent-emerald/70" />
        {title && (
          <span className="ml-3 font-mono text-xl text-white/50">{title}</span>
        )}
      </div>
      <pre className="overflow-x-auto bg-ink-800/60 p-6 font-mono text-xl leading-relaxed">
        {tokens.map((t, i) => (
          <span key={i} className={t.className}>
            {t.text}
          </span>
        ))}
      </pre>
    </div>
  );
}
